import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Brain, MoreVertical, Plus, Trash2, Trophy, Check } from 'lucide-react';
import ActivityLayout, {
    Toggle, SettingsGroup, SettingsRow, SubRow,
    InlineInput, AIButton, AIUndoButton, AIOptionButton, PollResults, AddImageSection, QuestionImage,
} from './ActivityLayout';
import Tooltip from '../../components/ui/Tooltip';
import styles from './ActivityLayout.module.css';
import useActivityContent from '../../hooks/useActivityContent';
import ParticipantExperience from '../join/ParticipantExperience';
import { API_PATH, request } from '../../config/api';
import { useConfirm } from '../../store/ConfirmDialogContext';

const EMPTY_QUESTION = () => ({
    id: Date.now(),
    text: '',
    options: ['', '', '', ''],
    correct: 0,
    description: '',
    imageUrl: null,
    showDescription: false,
    showImage: false,
});

const DUMMY_DATA = {
    questions: [{
        id: 'q1', text: 'Which country invented the game of Chess? ♟️',
        options: ['Greece', 'India', 'China', 'Persia'],
        correct: 1,
        description: 'Hint: this two-player strategy game was originally called Chaturanga and is played on a checkered board of 64 squares.',
        imageUrl: null, showDescription: true, showImage: false,
    }],
    settings: { timer: true, timerSeconds: 30, leaderboard: true, description: true, descText: 'Answer each question as fast as you can — speed and accuracy both matter!', pollResults: 'hidden', isLocked: false, allowUpdateResponse: false, imageUrl: null },
};

const Quiz = ({
    event, activity, isLaunched, onLaunch, onStop,
    onNext, onPrev, hasNext, hasPrev, onSettingsUpdated
}) => {
    const confirm = useConfirm();
    const [questions, setQuestions] = useState([EMPTY_QUESTION()]);
    const [settings, setSettings] = useState({
        timer: false,
        timerSeconds: 30,
        leaderboard: true,
        description: false,
        descText: '',
        pollResults: 'hidden',
        isLocked: false,
        allowUpdateResponse: true,
        imageUrl: null,
    });

    const { content, isLoading, isSaving, saveStatus, saveContent } =
        useActivityContent(event?.id, activity?.id);

    const [isImprovingIdx, setIsImprovingIdx] = useState(null);
    const [isImprovingQuestionDescIdx, setIsImprovingQuestionDescIdx] = useState(null);
    const [isAddingOptionsIdx, setIsAddingOptionsIdx] = useState(null);
    const [openMenuQuestionIdx, setOpenMenuQuestionIdx] = useState(null);
    const [menuAnchorRect, setMenuAnchorRect] = useState(null);
    const [previousQuestionText, setPreviousQuestionText] = useState({});
    const [previousQuestionDesc, setPreviousQuestionDesc] = useState({});
    const API_BASE = API_PATH;

    const handleImproveWithAI = async (qi) => {
        const questionText = questions[qi].text;
        if (!questionText.trim()) return;
        setIsImprovingIdx(qi);
        try {
            const res = await request(`${API_BASE}/ai/suggest`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ type: 'improve', text: questionText }),
            });
            if (res.ok) {
                const data = await res.json();
                if (data.suggestion) {
                    setPreviousQuestionText(prev => ({ ...prev, [qi]: questionText }));
                    updateQ(qi, 'text', data.suggestion);
                }
            } else {
                alert('Failed to improve with AI. Please try again.');
            }
        } catch (err) {
            console.error(err);
            alert('Failed to improve with AI. Please try again.');
        } finally {
            setIsImprovingIdx(null);
        }
    };

    const handleUndoQuestion = (qi) => {
        if (previousQuestionText[qi] !== undefined) {
            updateQ(qi, 'text', previousQuestionText[qi]);
            setPreviousQuestionText(prev => {
                const next = { ...prev };
                delete next[qi];
                return next;
            });
        }
    };

    const handleAddOptionsWithAI = async (qi) => {
        setIsAddingOptionsIdx(qi);
        try {
            const res = await request(`${API_BASE}/ai/suggest`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ type: 'options', context: { question: questions[qi].text } }),
            });
            if (res.ok) {
                const data = await res.json();
                if (data.suggestions) {
                    const qs = [...questions];
                    qs[qi].options = [...qs[qi].options, ...data.suggestions];
                    setQuestions(qs);
                }
            } else {
                alert('Failed to generate options. Please try again.');
            }
        } catch (err) {
            console.error(err);
            alert('Failed to generate options. Please try again.');
        } finally {
            setIsAddingOptionsIdx(null);
        }
    };

    const handleImproveQuestionDescWithAI = async (qi) => {
        const text = (questions[qi].description || '').trim();
        if (!text) return;
        setIsImprovingQuestionDescIdx(qi);
        try {
            const res = await request(`${API_BASE}/ai/suggest`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ type: 'improve', text }),
            });
            if (res.ok) {
                const data = await res.json();
                if (data.suggestion) {
                    setPreviousQuestionDesc(prev => ({ ...prev, [qi]: text }));
                    updateQ(qi, 'description', data.suggestion);
                }
            } else {
                alert('Failed to improve with AI. Please try again.');
            }
        } catch (err) {
            console.error(err);
            alert('Failed to improve with AI. Please try again.');
        } finally {
            setIsImprovingQuestionDescIdx(null);
        }
    };

    const handleUndoQuestionDesc = (qi) => {
        if (previousQuestionDesc[qi] !== undefined) {
            updateQ(qi, 'description', previousQuestionDesc[qi]);
            setPreviousQuestionDesc(prev => {
                const next = { ...prev };
                delete next[qi];
                return next;
            });
        }
    };

    useEffect(() => {
        if (!content || Object.keys(content).length === 0) return;
        if (content.questions) {
            setQuestions(content.questions.map((q) => ({
                ...q,
                description: q.description ?? '',
                imageUrl: q.imageUrl ?? null,
                showDescription: q.showDescription ?? !!(q.description && String(q.description).trim()),
                showImage: q.showImage ?? !!q.imageUrl,
            })));
        }
        if (content.settings) setSettings(s => ({ ...s, ...content.settings }));
    }, [content]);

    const set = (k, v) => setSettings((s) => ({ ...s, [k]: v }));

    const addQuestion = () => setQuestions([...questions, EMPTY_QUESTION()]);
    const removeQuestion = (i) => setQuestions(questions.filter((_, j) => j !== i));
    const updateQ = (i, field, val) => {
        const qs = [...questions];
        qs[i] = { ...qs[i], [field]: val };
        setQuestions(qs);
    };
    const toggleQuestionDescription = async (qi) => {
        const q = questions[qi];
        if (q.showDescription) {
            const ok = await confirm({
                title: 'Remove Description',
                message: 'Remove description from this question?',
                confirmText: 'Remove',
                tone: 'danger',
            });
            if (!ok) return;
        }
        const show = !q.showDescription;
        const qs = [...questions];
        qs[qi] = { ...qs[qi], showDescription: show, ...(show ? {} : { description: '' }) };
        setQuestions(qs);
        setOpenMenuQuestionIdx(null);
        setMenuAnchorRect(null);
    };
    const toggleQuestionImage = async (qi) => {
        const q = questions[qi];
        if (q.showImage) {
            const ok = await confirm({
                title: 'Remove Photo',
                message: 'Remove photo from this question?',
                confirmText: 'Remove',
                tone: 'danger',
            });
            if (!ok) return;
        }
        const show = !q.showImage;
        const qs = [...questions];
        qs[qi] = { ...qs[qi], showImage: show, ...(show ? {} : { imageUrl: null }) };
        setQuestions(qs);
        setOpenMenuQuestionIdx(null);
        setMenuAnchorRect(null);
    };
    const handleRemoveQuestion = async (qi) => {
        const ok = await confirm({
            title: 'Delete Question',
            message: 'Delete this question? This cannot be undone.',
            confirmText: 'Delete',
            tone: 'danger',
        });
        if (!ok) return;
        removeQuestion(qi);
        setOpenMenuQuestionIdx(null);
        setMenuAnchorRect(null);
    };
    const openMenu = (qi, e) => {
        e.stopPropagation();
        const rect = e.currentTarget.getBoundingClientRect();
        setMenuAnchorRect(rect);
        setOpenMenuQuestionIdx(qi);
    };
    const closeMenu = () => {
        setOpenMenuQuestionIdx(null);
        setMenuAnchorRect(null);
    };
    const updateOption = (qi, oi, val) => {
        const qs = [...questions];
        qs[qi].options[oi] = val;
        setQuestions(qs);
    };
    const addOption = (qi) => {
        const qs = [...questions];
        qs[qi] = { ...qs[qi], options: [...(qs[qi].options || []), ''] };
        setQuestions(qs);
    };
    const removeOption = async (qi, oi) => {
        const responseCount = activity?.response_count || 0;
        const ok = await confirm({
            title: 'Delete Option',
            message: 'Delete this option?',
            details: responseCount > 0 ? (
                <>
                    <span style={{ fontWeight: 600 }}>⚠️ This quiz already has {responseCount} response{responseCount !== 1 ? 's' : ''}.</span>
                    <br />
                    If any of them chose this option, it will no longer appear in the results breakdown.
                </>
            ) : undefined,
            confirmText: 'Delete',
            tone: 'danger',
        });
        if (!ok) return;
        const qs = [...questions];
        const opts = (qs[qi].options || []).filter((_, j) => j !== oi);
        if (opts.length < 2) return;
        let correct = qs[qi].correct;
        if (correct === oi) correct = 0;
        else if (correct > oi) correct -= 1;
        qs[qi] = { ...qs[qi], options: opts, correct };
        setQuestions(qs);
    };

    const right = (
        <>
            <SettingsGroup title="Quiz Settings">
                {/* Timer: per question (best practice) */}
                <SettingsRow label="Timer per question" description="Time limit for each question; countdown resets when moving to the next">
                    <Toggle checked={settings.timer} onChange={(e) => set('timer', e.target.checked)} />
                </SettingsRow>
                {settings.timer && (
                    <SubRow>
                        <span>Seconds per question:</span>
                        <InlineInput
                            type="number"
                            value={settings.timerSeconds}
                            onChange={(e) => set('timerSeconds', Math.min(300, Math.max(5, Number(e.target.value) || 30)))}
                            min={5}
                            max={300}
                        />
                    </SubRow>
                )}

                {/* Leaderboard */}
                <SettingsRow label="Add leaderboard" description="Show leaderboard after each question to build competition">
                    <Toggle checked={settings.leaderboard} onChange={(e) => set('leaderboard', e.target.checked)} />
                </SettingsRow>
            </SettingsGroup>

            <SettingsGroup title="Poll Results">
                <PollResults value={settings.pollResults} onChange={(v) => set('pollResults', v)} />
                <SettingsRow label="Allow participants to update response" description="Let participants change their answer after submitting">
                    <Toggle checked={settings.allowUpdateResponse !== false} onChange={(e) => set('allowUpdateResponse', e.target.checked)} />
                </SettingsRow>
            </SettingsGroup>
        </>
    );

    const left = (
        <>
            {questions.map((q, qi) => (
                <div key={q.id}>
                    {/* Question Card */}
                    <div className={q.showImage && q.imageUrl ? `${styles.questionCard} ${styles.questionCardWithImage}` : styles.questionCard}>
                        {q.showImage && q.imageUrl && (
                            <QuestionImage imageUrl={q.imageUrl} onImageChange={(url) => updateQ(qi, 'imageUrl', url)} />
                        )}
                        <div className={styles.questionContent}>
                            <div className={styles.questionLabelRow}>
                                <span className={styles.questionLabel}>Question {qi + 1} <span className={styles.requiredStar}>*</span></span>
                                <div className={styles.aiActions}>
                                    <AIButton onClick={() => handleImproveWithAI(qi)} loading={isImprovingIdx === qi} />
                                    {previousQuestionText[qi] !== undefined && (
                                        <AIUndoButton onClick={() => handleUndoQuestion(qi)} label="Undo" />
                                    )}
                                    <div className={styles.quizQuestionMenuWrap}>
                                        <Tooltip title="Question options" placement="left">
                                            <button
                                                type="button"
                                                className={styles.quizQuestionMenuBtn}
                                                onClick={(e) => openMenuQuestionIdx === qi ? closeMenu() : openMenu(qi, e)}
                                                aria-haspopup="menu"
                                                aria-expanded={openMenuQuestionIdx === qi}
                                                aria-label="Question options"
                                            >
                                                <MoreVertical size={18} />
                                            </button>
                                        </Tooltip>
                                    </div>
                                </div>
                            </div>
                            <input
                                className={styles.questionInput}
                                value={q.text}
                                onChange={(e) => updateQ(qi, 'text', e.target.value)}
                                placeholder="Type your question here..."
                            />
                            {settings.timer && (
                                <div className={styles.questionDescription}>
                                    <SubRow>
                                        <span>Time for this question (sec, optional):</span>
                                        <InlineInput
                                            type="number"
                                            value={q.timerSeconds ?? ''}
                                            onChange={(e) => {
                                                const v = e.target.value.trim();
                                                updateQ(qi, 'timerSeconds', v === '' ? undefined : Math.min(300, Math.max(5, Number(v) || 30)));
                                            }}
                                            placeholder={String(settings.timerSeconds || 30)}
                                            min={5}
                                            max={300}
                                        />
                                    </SubRow>
                                </div>
                            )}
                            {q.showDescription && (
                                <div className={styles.questionDescription}>
                                    <div className={styles.questionLabelRow}>
                                        <span className={styles.descLabel}>Question description (optional)</span>
                                        <span className={styles.aiActions}>
                                            <AIButton
                                                onClick={() => handleImproveQuestionDescWithAI(qi)}
                                                loading={isImprovingQuestionDescIdx === qi}
                                            />
                                            {previousQuestionDesc[qi] !== undefined && (
                                                <AIUndoButton onClick={() => handleUndoQuestionDesc(qi)} label="Undo" />
                                            )}
                                        </span>
                                    </div>
                                    <input
                                        className={styles.descInput}
                                        value={q.description || ''}
                                        onChange={(e) => updateQ(qi, 'description', e.target.value)}
                                        placeholder="Extra hint or context for this question..."
                                    />
                                </div>
                            )}
                            {q.showImage && !q.imageUrl && (
                                <div className={styles.questionDescription}>
                                    <AddImageSection
                                        imageUrl={q.imageUrl}
                                        onImageChange={(url) => updateQ(qi, 'imageUrl', url)}
                                    />
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Options */}
                    <div className={styles.optionsCard}>
                        <div className={styles.optionsHeader}>
                            <span className={styles.optionsLabel}>Options — click row to mark correct answer</span>
                            <div className={styles.optionsHeaderActions}>
                                <AIOptionButton
                                    onClick={() => handleAddOptionsWithAI(qi)}
                                    loading={isAddingOptionsIdx === qi}
                                    label="Suggest with AI"
                                />
                                <button
                                    type="button"
                                    className={styles.addOptionBtn}
                                    onClick={() => addOption(qi)}
                                >
                                    <Plus size={14} /> Add option
                                </button>
                            </div>
                        </div>
                        {(q.options || []).map((opt, oi) => (
                            <div
                                key={oi}
                                className={`${styles.optionRow} ${q.correct === oi ? styles.optionRowCorrect : ''}`}
                                onClick={() => updateQ(qi, 'correct', oi)}
                            >
                                <button
                                    type="button"
                                    className={`${styles.optionLetterBtn} ${q.correct === oi ? styles.optionLetterBtnCorrect : ''}`}
                                    onClick={(e) => { e.stopPropagation(); updateQ(qi, 'correct', oi); }}
                                    aria-label={q.correct === oi ? `Option ${String.fromCharCode(65 + oi)} is the correct answer` : `Mark option ${String.fromCharCode(65 + oi)} as correct`}
                                    aria-pressed={q.correct === oi}
                                >
                                    {String.fromCharCode(65 + oi)}
                                </button>
                                <input
                                    className={`${styles.optionInput} ${q.correct === oi ? styles.optionInputCorrect : ''}`}
                                    value={opt}
                                    onChange={(e) => updateOption(qi, oi, e.target.value)}
                                    placeholder={`Option ${String.fromCharCode(65 + oi)}`}
                                    onClick={(e) => e.stopPropagation()}
                                />
                                {q.correct === oi && (
                                    <span className={styles.correctBadge}><Check size={10} /> Correct</span>
                                )}
                                {(q.options || []).length > 2 && (
                                    <Tooltip title="Remove this option" placement="top">
                                        <button
                                            type="button"
                                            className={styles.optionRemoveBtn}
                                            onClick={(e) => { e.stopPropagation(); removeOption(qi, oi); }}
                                            aria-label="Remove option"
                                        >
                                            <Trash2 size={14} />
                                        </button>
                                    </Tooltip>
                                )}
                            </div>
                        ))}
                    </div>

                    {qi < questions.length - 1 && (
                        <div className={styles.questionSeparator} />
                    )}
                </div>
            ))}

            {openMenuQuestionIdx != null && menuAnchorRect && typeof document !== 'undefined' && createPortal(
                <>
                    <div className={styles.quizQuestionMenuBackdrop} onClick={closeMenu} aria-hidden />
                    <div
                        className={styles.quizQuestionMenu}
                        role="menu"
                        style={{
                            position: 'fixed',
                            top: menuAnchorRect.bottom + 6,
                            left: Math.min(menuAnchorRect.right - 180, window.innerWidth - 200),
                            right: 'auto',
                            minWidth: 200,
                        }}
                    >
                        <button type="button" className={styles.quizQuestionMenuItem} role="menuitem" onClick={() => toggleQuestionDescription(openMenuQuestionIdx)}>
                            {questions[openMenuQuestionIdx]?.showDescription ? 'Remove description' : 'Add description'}
                        </button>
                        <button type="button" className={styles.quizQuestionMenuItem} role="menuitem" onClick={() => toggleQuestionImage(openMenuQuestionIdx)}>
                            {questions[openMenuQuestionIdx]?.showImage ? 'Remove photo' : 'Add photo'}
                        </button>
                        {questions.length > 1 && (
                            <button type="button" className={`${styles.quizQuestionMenuItem} ${styles.quizQuestionMenuDanger}`} role="menuitem" onClick={() => handleRemoveQuestion(openMenuQuestionIdx)}>
                                Delete question
                            </button>
                        )}
                    </div>
                </>,
                document.body
            )}

            {/* Add Question */}
            <button type="button" className={styles.addQuizQuestionBtn} onClick={addQuestion}>
                <Plus size={15} /> Add another quiz question
            </button>
        </>
    );

    const q = questions[0];
    const loadDummy = () => {
        setQuestions(DUMMY_DATA.questions);
        setSettings(s => ({ ...s, ...DUMMY_DATA.settings }));
    };

    const participantPreview = (
        <ParticipantExperience
            step="activity"
            name="Preview User"
            event={event}
            activity={{
                type: 'quiz',
                title: 'Quiz',
                content: { questions, settings }
            }}
        />
    );

    return (
        <ActivityLayout
            icon={<Brain size={20} />}
            color="var(--cat-rose)"
            title="Quiz"
            eventName={event?.name}
            event={event}
            activity={activity}
            left={left}
            right={right}
            participantPreview={participantPreview}
            validate={() => !questions[0]?.text?.trim() ? 'First question text is required.' : null}
            onSave={() => {
                setPreviousQuestionText({});
                setPreviousQuestionDesc({});
                saveContent({ questions, settings });
            }}
            onLoadExample={loadDummy}
            isSaving={isSaving}
            saveStatus={saveStatus}
            isLoading={isLoading}
            isLaunched={isLaunched}
            onLaunch={onLaunch}
            onStop={onStop}
            onNext={onNext}
            onPrev={onPrev}
            hasNext={hasNext}
            hasPrev={hasPrev}
            onSettingsUpdated={onSettingsUpdated}
        />
    );
};

export default Quiz;
